import React, { useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation } from 'swiper'
import { ArrowLeft, ArrowRight } from '@next-nx/shared-ui/icons'
import { Button, WContainer, WImage } from '@next-nx/shared-ui/atoms'
import { useTealium } from '@next-nx/hooks'
import { getTimestamp, getAnchorRel } from '@next-nx/utils'
import styles from './BrandsSection.module.sass'
import Link from 'next/link'
import { MarcaVeicoloType } from '@next-nx/utils/types'

SwiperCore.use([Navigation])

interface Props {
  titolo?: string
  ctaLabel?: string
  url?: string
  brands?: MarcaVeicoloType[]
  className?: string
}

const BrandsSection = (props: Props) => {
  const { titolo = '', ctaLabel = '', url = null, brands = [], className = '' } = props

  const navigationPrevRef = useRef(null)
  const navigationNextRef = useRef(null)
  const { linkDataLayer } = useTealium()

  const handleTracking = (action_type: string = '') => {
    const action_timestamp = getTimestamp()

    linkDataLayer({
      virtual: 'action',
      action_name: 'navigazione',
      action_tech: 'click',
      action_type: action_type.replace(/ /g, '_').toLowerCase(),
      action_timestamp,
      action_effect: 'interazione_componente_i_nostri_marchi',
    })
  }

  return brands.length > 0 ? (
    <div className={`${styles.root} ${className}`}>
      <WContainer>
        {(!!titolo || (!!ctaLabel && !!url)) && (
          <div className={styles.head}>
            <p className={styles.title}>{titolo}</p>
            {!!ctaLabel && !!url && (
              <Button
                label={ctaLabel}
                icon={<ArrowRight />}
                iconPos="right"
                variant="ghost"
                href={url}
                className={styles.ctaDesktop}
                onClick={() => handleTracking(ctaLabel)}
              />
            )}
          </div>
        )}
        <div className={styles.slider}>
          <button
            role="button"
            id="brands-section-prev"
            ref={navigationPrevRef}
            className={`${styles.nav} ${styles['nav--prev']}`}
          >
            <ArrowLeft />
          </button>
          <button
            role="button"
            id="brands-section-next"
            ref={navigationNextRef}
            className={`${styles.nav} ${styles['nav--next']}`}
          >
            <ArrowRight />
          </button>
          <Swiper
            slidesPerView={2.5}
            watchOverflow
            pagination={{ clickable: true }}
            spaceBetween={30}
            centerInsufficientSlides
            navigation={{
              prevEl: '#brands-section-prev',
              nextEl: '#brands-section-next',
            }}
            breakpoints={{
              576: {
                slidesPerView: 3.5,
              },
              768: {
                slidesPerView: 5.5,
              },
              992: {
                slidesPerView: 6,
              },
            }}
          >
            {brands.map((x) => (
              <SwiperSlide key={x.pk}>
                <div className={styles.slide}>
                  <Link href={x.url || ''}>
                    <a
                      className={styles.slideLink}
                      rel={getAnchorRel(x.url || '')}
                      onClick={() => handleTracking(x.nome)}
                    >
                      <div className={styles.slideImageBox}>
                        <WImage
                          src={x.image}
                          alt={x.nome}
                          title={x.nome}
                          maxWidth={109}
                          maxHeight={70}
                          layout="fill"
                          objectFit="contain"
                        />
                      </div>
                    </a>
                  </Link>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        {!!ctaLabel && !!url && (
          <Button
            label={ctaLabel}
            icon={<ArrowRight />}
            iconPos="right"
            variant="ghost"
            href={url}
            className={styles.ctaMobile}
          />
        )}
      </WContainer>
    </div>
  ) : (
    <></>
  )
}

export default BrandsSection
